/**
 * v3.nra — NRA document scanning (multi-document) + W-8BEN generation client.
 *
 * Backed by:
 *   - POST /api/v3/nra/scan (NraDocumentScanFunction)         — 8 doc types
 *   - POST /api/v3/nra/w8ben/generate (W8BenGenerationFunction) — gen pipeline
 *   - GET  /api/v3/nra/w8ben/{cifId}                         — list filed forms
 *
 * Per legacy procedure (FSR-Account-Opening-decoded.txt non-citizen section),
 * W-8BEN is GENERATED at NRA interest-bearing account opening, not scanned.
 * The scanner is for passport / visa / I-94 / I-20 / DS-2019 / Green Card /
 * EAD / SSN.
 */

import type { RequestOptions } from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

/**
 * NRA document tab/slot codes recognized by the scanner UI. Loose string
 * so capture-routing tables that ship newer slot codes (address-proof,
 * ssn-card, i20-variant) compile without churn. Server-side classifier
 * still validates the slug.
 */
export type NraDocumentType =
  | 'passport'
  | 'visa'
  | 'i94'
  | 'i20'
  | 'ds2019'
  | 'green-card'
  | 'ead'
  | 'ssn'
  | 'ssn-card'
  | 'address-proof'
  | 'primary-id'   // BACKLOG-0600 — US-Citizen / Permanent-Resident PRIMARY photo ID (cohort tile; prebuilt-idDocument self-classifies)
  | string;

export interface NraScanRequest {
  documentType: NraDocumentType;
  image: string;          // base64-encoded
  mimeType: string;       // image/jpeg | image/png | image/tiff | application/pdf
  cifId?: string;
  personDraftId?: string;
  cartId?: string;
}

export interface NraScanField {
  value: string | null;
  confidence: number;     // 0.0–1.0
  originalValue: string | null;
}

export interface NraScanFields {
  // Universal
  fullName?: NraScanField | null;
  firstName?: NraScanField | null;
  middleName?: NraScanField | null;
  lastName?: NraScanField | null;
  dateOfBirth?: NraScanField | null;
  countryOfCitizenship?: NraScanField | null;

  // Passport
  foreignPassportNumber?: NraScanField | null;
  foreignPassportCountry?: NraScanField | null;
  foreignPassportIssueDt?: NraScanField | null;
  foreignPassportExpDt?: NraScanField | null;
  machineReadableZone?: NraScanField | null;

  // Visa
  visaType?: NraScanField | null;
  visaIssuePost?: NraScanField | null;
  visaIssueDt?: NraScanField | null;
  visaExpDt?: NraScanField | null;
  visaEntries?: NraScanField | null;
  visaAnnotation?: NraScanField | null;
  /** CEAC control number (top-right of the foil) — NOT the red foil number. */
  visaControlNumber?: NraScanField | null;
  /** The visa's OWN document number — the red-printed foil number (often
   *  W-prefixed). Distinct from visaControlNumber (CEAC) and from the
   *  REFERENCED passport number, which lands in foreignPassportNumber as a
   *  cross-ref only (Bill smoke #2 fix, 2026-06-12). Additive optional. */
  visaDocumentNumber?: NraScanField | null;

  // I-94
  i94Number?: NraScanField | null;
  i94AdmitClass?: NraScanField | null;
  i94AdmitUntil?: NraScanField | null;
  /** Dedicated expiration-date leaf (2026-06-12) — distinct from i94AdmitUntil,
   *  which may carry the "D/S" (duration of status) literal instead of a date. */
  i94ExpirationDate?: NraScanField | null;
  i94EntryDate?: NraScanField | null;
  i94EntryPort?: NraScanField | null;

  // I-20
  sevisId?: NraScanField | null;
  schoolName?: NraScanField | null;
  schoolCode?: NraScanField | null;
  programStartDt?: NraScanField | null;
  programEndDt?: NraScanField | null;
  educationLevel?: NraScanField | null;
  major1?: NraScanField | null;
  major1CipCode?: NraScanField | null;
  formIssueReason?: NraScanField | null;

  // DS-2019
  programSponsorName?: NraScanField | null;
  programNumber?: NraScanField | null;
  exchangeVisitorCategory?: NraScanField | null;
  fieldCode?: NraScanField | null;
  twoYearResidenceFlag?: NraScanField | null;

  // Green Card
  greenCardANumber?: NraScanField | null;
  greenCardCategory?: NraScanField | null;
  greenCardExpDt?: NraScanField | null;
  greenCardResidentSince?: NraScanField | null;

  // EAD
  eadANumber?: NraScanField | null;
  eadCategory?: NraScanField | null;
  eadExpDt?: NraScanField | null;

  // SSN
  ssnNumber?: NraScanField | null;

  // Primary photo ID (US-Citizen / Permanent-Resident) — BACKLOG-0600.
  // Coded CIP slots from the prebuilt-idDocument core. A US driver's license
  // or US passport has no ForeignPassport* home, so its F254-258 identity-
  // verification values land here. pcbIdType is the Core Director F254
  // IdVerifyCode (1 DL / 2 State ID / 3 Passport / 4 Military / 5 SSN / 8 Other).
  pcbIdType?: NraScanField | null;        // F254
  pcbIdNumber?: NraScanField | null;      // F255
  pcbIdIssuer?: NraScanField | null;      // F256
  pcbIdIssueDate?: NraScanField | null;   // F257
  pcbIdExpiryDate?: NraScanField | null;  // F258

  /** Loose escape hatch — admin-authored field codes the typed slots
   *  haven't enumerated yet (sex, foreignAddress, et al.). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface NraScanResponse {
  success: boolean;
  documentType: NraDocumentType | string;
  modelId: string;
  fields: NraScanFields | null;
  needsConfirm: ReadonlyArray<string>;
  pageCount: number;
  trainingBlobId: string | null;
  error: string | null;
}

// W-8BEN GENERATION (separate from scanning)
export interface W8BenGenerateRequest {
  cifId: string;
  cartId?: string;
  personDraftId?: string;
  // Override values (otherwise pulled from CustInq + person fields)
  overrides?: {
    treatyCountry?: string;
    treatyArticle?: string;
    treatyRate?: number;
    treatyIncomeType?: string;
  };
}

export interface W8BenGenerateResponse {
  success: boolean;
  documentId?: string;          // ao_Documents row id
  templateCode?: string;        // resolved DocumentTemplate code
  pdfBlobUri?: string;          // ao_Documents BlobUri
  signingRequired: boolean;     // true → caller routes into SignNow ceremony
  signingSessionId?: string;
  error?: string;
}

export interface W8BenListItem {
  documentId: string;
  cifId: string;
  generatedAt: string;          // ISO
  signedAt: string | null;
  expiresAt: string | null;     // signedAt + nra.gen.w8ben.refreshYears
  treatyCountry: string | null;
  treatyArticle: string | null;
  treatyRate: number | null;
  status: 'draft' | 'pending-signature' | 'signed' | 'expired' | 'voided';
}

export interface W8BenListResponse {
  success: boolean;
  count: number;
  items: ReadonlyArray<W8BenListItem>;
}

export class NraApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * Run a single document through Azure Document Intelligence.
   *
   * Use the documentType discriminator to route the image to the right model
   * (prebuilt for passport/green-card/ead, custom-neural for i94/i20/ds2019/ssn).
   * The response carries per-field confidence; consumers MUST gate auto-fill
   * on `needsConfirm[]` so the FSR confirms low-confidence values.
   */
  public scan(body: NraScanRequest, opts?: RequestOptions): Promise<NraScanResponse> {
    return this.http.post<NraScanResponse>('/v3/nra/scan', { ...opts, body });
  }

  /**
   * Generate a W-8BEN PDF for an NRA opening an interest-bearing product.
   *
   * Pre-fills from CustInq + RaoPerson NRA fields + TreatyCountries lookup.
   * Returns ao_Documents row + signing session id (caller routes into the
   * SignNow ceremony for the rest of the cart's disclosures).
   */
  public generateW8Ben(body: W8BenGenerateRequest, opts?: RequestOptions): Promise<W8BenGenerateResponse> {
    return this.http.post<W8BenGenerateResponse>('/v3/nra/w8ben/generate', { ...opts, body });
  }

  /**
   * List W-8BEN documents on file for a CIF (for the 3-year refresh task +
   * NRA panel "currently filed" indicator).
   */
  public listW8Bens(cifId: string, opts?: RequestOptions): Promise<W8BenListResponse> {
    return this.http.get<W8BenListResponse>(`/v3/nra/w8ben/${encodeURIComponent(cifId)}`, opts);
  }

  /**
   * Classifier-only step — given a captured page (base64 image), return the
   * detected NRA document type + confidence. Used by the multi-tab capture
   * UI to route the page to the right slot before storing the full extract.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public classify(body: unknown, opts?: RequestOptions): Promise<any> {
    return this.http.post('/v3/nra/classify', { ...opts, body });
  }
}
